import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeftIcon, TagIcon, SparklesIcon } from '@heroicons/react/24/outline';
import OptimizedImage from '../components/ui/OptimizedImage';

const CategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/categories');
      setCategories(response.data.categories || []);
      setError(null);
    } catch (err) {
      setError('שגיאה בטעינת הקטגוריות');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchCategories}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  // Group categories by parent
  const parentCategories = categories.filter(cat => !cat.parent_id);
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section - Mobile First */}
      <div className="bg-gradient-to-b from-primary-600 to-primary-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-2 sm:p-3 bg-white/10 rounded-full mb-4">
              <TagIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
              קטגוריות מוצרים
            </h1>
            <p className="text-base sm:text-lg text-primary-100 max-w-2xl mx-auto">
              גלה את המוצרים שלנו לפי קטגוריות - מאות מוצרים באיכות מעולה
            </p>
          </div>
        </div>
      </div>

      {/* Categories Grid - Mobile First: 2 columns, then scale up */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Featured Categories Badge */}
        {parentCategories.filter(cat => cat.is_featured).length > 0 && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 mb-4">
              <SparklesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-500" />
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                קטגוריות מומלצות
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
              {parentCategories.filter(cat => cat.is_featured).map((category) => (
                <CategoryCard key={category.id} category={category} featured />
              ))}
            </div>
          </div>
        )}

        {/* All Categories */}
        <div className="mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
            כל הקטגוריות
          </h2>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {parentCategories.map((category) => (
            <CategoryCard key={category.id} category={category} />
          ))}
        </div>

        {/* Empty State */}
        {parentCategories.length === 0 && (
          <div className="text-center py-12 sm:py-16">
            <div className="text-gray-400 mb-4">
              <TagIcon className="h-16 w-16 sm:h-20 sm:w-20 mx-auto" />
            </div>
            <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">
              אין קטגוריות זמינות
            </h3>
            <p className="text-sm sm:text-base text-gray-500">
              הקטגוריות יתווספו בקרוב
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile-First Category Card Component
const CategoryCard = ({ category, featured = false }) => {
  const subcategories = category.subcategories || [];
  const productCount = category.product_count || 0;

  return (
    <Link
      to={`/products?category_id=${category.id}`}
      className={`group relative bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${
        featured ? 'ring-2 ring-yellow-400' : ''
      }`}
    >
      {/* Featured Badge */}
      {featured && (
        <div className="absolute top-2 right-2 z-10 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
          <SparklesIcon className="h-3 w-3" />
          <span className="hidden sm:inline">מומלץ</span>
        </div>
      )}

      {/* Category Image - Square aspect ratio for consistency */}
      <div className="aspect-square bg-gradient-to-br from-primary-50 to-primary-100 relative overflow-hidden">
        {category.image ? (
          <OptimizedImage
            src={category.image}
            alt={category.name}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            lazy={true}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TagIcon className="h-12 w-12 sm:h-16 sm:w-16 text-primary-300" />
          </div>
        )}
        
        {/* Gradient Overlay - Mobile optimized */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Category Info - Mobile First Sizing */}
      <div className="p-3 sm:p-4">
        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1 text-sm sm:text-base group-hover:text-primary-600 transition-colors">
          {category.name}
        </h3>
        
        {/* Product Count */}
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
          <span>{productCount} מוצרים</span>
          <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 group-hover:translate-x-1 transition-transform" />
        </div>

        {/* Subcategories Count - Hidden on very small screens */}
        {subcategories.length > 0 && (
          <div className="mt-2 hidden sm:block">
            <span className="text-xs text-primary-600 font-medium">
              {subcategories.length} תתי-קטגוריות
            </span>
          </div>
        )}
      </div>
    </Link>
  );
};

export default CategoryPage;
